import { applyPatch, flow, getSnapshot, isAlive, onPatch, resolvePath, resolveIdentifier, types } from "mobx-state-tree";
import { DEBUG_HISTORY } from "../../lib/debug";
import { TileModel } from "../tiles/tile-model";
/**
 * Error thrown when applyPatch fails partway through a batch.
 * Includes the number of patches that were successfully applied so callers
 * can identify which input patch caused the failure. The caller is
 * responsible for rolling back the partial application using the inverse
 * patches it already has access to (e.g. from history entry records).
 */
export class PatchApplicationError extends Error {
    constructor(numApplied, originalError) {
        const message = originalError instanceof Error ? originalError.message : String(originalError);
        super(`Patch application failed after ${numApplied} patches: ${message}`);
        Object.defineProperty(this, "numApplied", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.name = "PatchApplicationError";
        this.numApplied = numApplied;
    }
}
export const Tree = types.model("Tree", {})
    .volatile(self => ({
    applyingManagerPatches: false,
    treeManagerAPI: undefined
}))
    .views(self => ({
    get treeId() {
        throw new Error("Trees need to provide a treeId property");
    }
}))
    .actions(self => ({
    updateTreeAfterSharedModelChanges(options) {
        // If there is no sharedModel param, run the update function on all tiles which
        // have shared model references.
        // If there is a sharedModel param, only run the update function on tiles which
        // have this shared model.
        // If we don't have a document let the exception happen so we
        // can track this down
        const document = self.content;
        // FIXME: In the case where several changes have been applied we need to notify
        // any new tiles that were just linked and any old tiles that were unlinked
        // This will happen when there is no specific shared model being updated.
        // We can probably punt this case for later.
        // FIXME: when we start deleting shared models the code below will have to
        // be updated to handle this case.
        // We can probably optimize this by using a MSTView to cache the tiles
        // here. But I don't remember how it handles parameter values
        const tiles = [];
        let sharedModel;
        if (options) {
            sharedModel = options.sharedModel;
            const newSharedModelEntry = document.sharedModelMap.get(sharedModel.id);
            if (!newSharedModelEntry) {
                console.warn(`no shared model entry for shared model: ${sharedModel.id}`);
            }
            else {
                tiles.push(...newSharedModelEntry.tiles);
            }
            const initialSharedModelEntry = options.initialSharedModelMap[sharedModel.id];
            if (!initialSharedModelEntry) {
                // This entry didn't exist before
            }
            else {
                initialSharedModelEntry.tiles.forEach(tileId => {
                    if (tiles.find(existingTile => existingTile.id === tileId)) {
                        // This tile has already been added to our list
                    }
                    else {
                        const tile = resolveIdentifier(TileModel, document, tileId);
                        if (tile) {
                            tiles.push(tile);
                        }
                    }
                });
            }
        }
        else {
            // Only include tiles that have at least one shared model
            document.sharedModelMap.forEach(sharedModelEntry => {
                sharedModelEntry.tiles.forEach(tile => {
                    if (!tiles.includes(tile)) {
                        tiles.push(tile);
                    }
                });
            });
        }
        // Run update function on the tiles
        for (const tile of tiles) {
            if (isAlive(tile)) {
                tile.setUpdatedHash();
                tile.content.updateAfterSharedModelChanges(sharedModel);
            }
        }
    }
}))
    .actions(self => {
    const updateTreeAfterSharedModelChangesInternal = (sharedModel, initialSharedModelMap) => {
        // If we are applying manager patches, then we ignore any sync actions
        // otherwise the user might make a change such as changing the name of a
        // node while the patches are applied. When they do this the patch for
        // the shared model might have been applied first, and which if sync is
        // enabled could create a new node in the diagram. Then the patch for the
        // diagram is applied which also creates a new node in the diagram.
        // Even if we just disable the sync when the shared model update is done
        // from the patch, if the user makes a change, this would be a separate
        // action would would trigger the sync. So if the user made this change
        // at just the right time it would could result in duplicate nodes in the
        // diagram.
        if (self.applyingManagerPatches) {
            return;
        }
        // The TreeMonitor middleware should pickup the historyEntryId and
        // exchangeId parameters automatically. And then when it sends any
        // changes captured during the update, it should include these ids
        if (isAlive(self)) {
            self.updateTreeAfterSharedModelChanges({ sharedModel, initialSharedModelMap });
        }
    };
    return {
        updateTreeAfterSharedModelChangesInternal
    };
})
    .actions(self => {
    return {
        //
        // Special actions called by the framework. These define the Tree API.
        //
        // This will be called by the manager when a shared model tree changes
        applySharedModelSnapshotFromManager(historyEntryId, exchangeId, snapshot) {
            throw new Error("not implemented yet");
        },
        // The manager calls this before it calls applyPatchesFromManager
        startApplyingPatchesFromManager(historyEntryId, exchangeId) {
            self.applyingManagerPatches = true;
            // We return a promise because the API is async
            // The action itself doesn't do anything asynchronous though
            // so it isn't necessary to use a flow
            return Promise.resolve();
        },
        // Actually apply the patches.
        // It might be called multiple times after startApplyingPatchesFromManager.
        //
        // Patches are applied as a single batch for performance. MST's applyPatch
        // wraps the array in one action, which means middlewares (like the
        // TreeMonitor) fire once for the whole batch instead of once per patch.
        // With hundreds or thousands of patches during history playback, applying
        // them one at a time would be significantly slower.
        //
        // However, batch applyPatch doesn't tell us which patch failed if an
        // error occurs partway through. To work around this, we register an
        // onPatch listener that counts successfully applied patches. On
        // failure, we throw a PatchApplicationError containing the count so
        // the caller can identify which input patch caused the failure. The
        // caller is responsible for rolling back the partial application
        // using the inverse patches it already has (e.g. from the history
        // entry records that produced the batch).
        //
        // MST treats a patch with an empty or missing path as a whole-tree
        // snapshot replacement. Applying such a patch produces multiple onPatch
        // calls (one per field in the resulting snapshot) which would break our
        // 1:1 mapping between input patches and onPatch events. We reject both
        // the undefined/missing case AND the empty-string case upfront because
        // neither form should appear in recorded history patches, and either
        // would silently corrupt the numApplied counter.
        applyPatchesFromManager(historyEntryId, exchangeId, patchesToApply) {
            for (const patch of patchesToApply) {
                if (!patch.path) {
                    // Throw PatchApplicationError (with numApplied=0) so this
                    // case flows through the playback-failure handling path in
                    // TreeManager.goToHistoryEntry, rather than escaping as an
                    // unhandled error that bypasses rollback and failure
                    // recording.
                    throw new PatchApplicationError(0, new Error("History patches must have a non-empty path. " +
                        "Pathless or root-snapshot patches are not supported."));
                }
            }
            let numApplied = 0;
            const disposer = onPatch(self, () => {
                numApplied++;
            });
            try {
                applyPatch(self, patchesToApply);
            }
            catch (e) {
                throw new PatchApplicationError(numApplied, e);
            }
            finally {
                disposer();
            }
            // We return a promise because the API is async
            // The action itself doesn't do anything asynchronous though
            // so it isn't necessary to use a flow
            return Promise.resolve();
        },
        // The manager calls this after all patches have been applied
        finishApplyingPatchesFromManager(historyEntryId, exchangeId) {
            self.applyingManagerPatches = false;
            // TODO: Need to deal with possible effects on the undo stack
            //
            // If all of the patches applied correctly and the user didn't inject any
            // changes while the patches were applying, then everything should be
            // fine. There should be nothing to updated by
            // updateTreeAfterSharedModelChanges
            //
            // However, if the user made a change in the shared model like deleting a
            // node while the patches were being applied this would make the shared
            // model be out of sync with the tree. The tree would not be updated
            // before now because applyingManagerPatches is true. So that deleted
            // node change would get applied here. When it is applied it would
            // generate a new undoable action that is not grouped with the action that
            // deleted the node from the shared model. So now if the user undoes, the
            // actions will not get undone together. This will probably result in a
            // broken UI for the user.
            //
            // We could record the action id of any actions that happen while the
            // patches are being applied. It is possible that multiple actions could
            // happen. Because we aren't running the updateTreeAfterSharedModelChanges
            // after each of these actions, we wouldn't be able to tell what tree
            // updates are associated with which of the multiple actions.
            //
            // I think the best thing to do is:
            // - merge any actions that happened during the patch application into a
            //   single action. So basically combine their patches.
            // - use the id of that combined action for any changes the
            //   updateTreeAfterSharedModelChanges causes here.
            //
            // If there were no injected or intermediate actions, but for some reason
            // this update function does make changes in the tree, what should we do?
            // We should at least log this issue to the console, so we can try to
            // track down what happened. One likely reason is a broken implementation
            // of the updateTreeAfterSharedModelChanges. And that will be likely to
            // happen during development.
            self.updateTreeAfterSharedModelChanges();
            // We return a promise because the API is async
            // The action itself doesn't do anything asynchronous though
            // so it isn't necessary to use a flow
            return Promise.resolve();
        },
    };
})
    .actions(self => ({
    handleSharedModelChanges: flow(function* handleSharedModelChanges(historyEntryId, exchangeId, call, sharedModelPath, initialSharedModelMap) {
        // The sharedModelPath is to the sharedModel entry we have to tack on /sharedModel to
        // get the actual model object that was just updated.
        // TODO: see if there is a helper method to join paths for us
        const model = resolvePath(self, `${sharedModelPath}/sharedModel`);
        // Note: the environment of the call will be undefined because the undoRecorder cleared
        // it out before calling this function
        if (DEBUG_HISTORY) {
            // eslint-disable-next-line no-console
            console.log(`observed changes in sharedModel: ${model.id} of tree: ${self.treeId}`, { historyEntryId, action: call });
        }
        if (!self.treeManagerAPI) {
            console.warn("handleSharedModelChanges before a treeManagerAPI is set");
            return;
        }
        // When this is called by the TreeManager, we want to do the internal
        // shared model sync, but we don't want to resend the snapshot back to the
        // manager.
        if (call.name !== "applySharedModelSnapshotFromManager") {
            // TODO: figure out if we should be recording this special action in the undo
            // stack
            const snapshot = getSnapshot(model);
            // TODO: we use the exchangeId from the original exchange here so we
            // need to wait for the manager to confirm this updateSharedModel call
            // before we can continue. Otherwise the manager might receive the final
            // addTreePatchRecord before it gets any shared model updates. Currently
            // updateSharedModel waits for all of the dependent trees to update
            // their shared models before returning, so this might cause a long
            // delay.
            //
            // We could start a new exchange with the manager and just wait for
            // that, and then call updateSharedModel with the exchangeId for this
            // new exchange.
            //
            // Or we could add a new option to updateSharedModel so in some cases it
            // waits for all of the dependent trees to be updated and in other cases
            // it just waits for the manager to confirm it received the request.
            //
            // It might also be possible that we can change the async flow of
            // applying history events so it isn't necessary for the trees to wait
            // for the shared model to be fully updated. So then this
            // updateSharedModel call can just wait for a confirmation in all cases.
            //
            // - Q: Why is the exchangeId passed to updateSharedModel
            // - A: It isn't really needed but it is useful for debugging.
            //   updateSharedModel makes a new exchangeId for each tree that it
            //   sends the shared model to. It doesn't do anything with the passed
            //   in exchangeId.
            //
            // Note that the TreeMonitor takes care of closing the exchangeId used
            // here. This same exchangeId is passed to all the shared model
            // callbacks and then they are all waited for, and finally the exchange
            // is closed.
            //
            yield self.treeManagerAPI.updateSharedModel(historyEntryId, exchangeId, self.treeId, snapshot);
        }
        // Notify the tiles about the shared model update.
        //
        // TODO: an inefficiency  with this approach is that we are
        // treating all changes within the sharedModelPath the same.
        // The change in the shared model might not require the tree's
        // tiles to update any of their state.
        //
        // There is probably a way to use the mobx internals so we can
        // track what updateTreeAfterSharedModelChanges references and
        // only run it when one of those things have changed.
        //
        // TODO: This should not cause a loop because the implementation
        // of updateTreeAfterSharedModelChanges should not modify
        // the shared model or shared model view that triggered this
        // handler in the first place. However a developer might make
        // a mistake. So it would be useful if we could identify the
        // looping and notify them.
        if (isAlive(self)) {
            self.updateTreeAfterSharedModelChangesInternal(model, initialSharedModelMap);
        }
    })
}));
//# sourceMappingURL=data:application/json;base64,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